import { useState, useMemo, useCallback, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery, usePaginationFragment, useMutation } from 'react-relay';
import { Header } from '@/components/Header';
import { EpisodeList, type EpisodeList_episode$data } from '@/components/EpisodeList';
import { Search } from '@/components/Search';
import { SEO } from '@/components/SEO';
import { usePlayer } from '@/lib/PlayerContext';
import { useAuth } from '@/lib/auth/AuthContext';
import type { PodcastPageQuery } from '@/__generated__/PodcastPageQuery.graphql';
import type { PodcastPage_topic$key } from '@/__generated__/PodcastPage_topic.graphql';
import type { PodcastPageFollowMutation } from '@/__generated__/PodcastPageFollowMutation.graphql';
import type { PodcastPageUnfollowMutation } from '@/__generated__/PodcastPageUnfollowMutation.graphql';

/**
 * Root query for the PodcastPage - fetches topic and initial episodes
 */
const podcastPageQuery = graphql`
  query PodcastPageQuery($slug: String!) {
    topic(slug: $slug) {
      ...PodcastPage_topic
    }
  }
`;

/**
 * Fragment for topic data with paginated episodes.
 * Episodes include all fields needed by EpisodeList for search and rendering.
 */
const PodcastPage_topic = graphql`
  fragment PodcastPage_topic on Topic
  @refetchable(queryName: "PodcastPageEpisodesQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 20 }
    after: { type: "String" }
  ) {
    id
    slug
    name
    voiceId
    stylePrompt
    customHostName
    customHostPersonality
    s3IntroPath
    s3OutroPath
    s3AnchorImagePath
    spotifyUrl
    applePodcastsUrl
    isActive
    visibility
    isFollowedByViewer
    followerCount
    episodes(first: $first, after: $after)
      @connection(key: "PodcastPage_episodes") {
      edges {
        node {
          id
          episodeId
          ...EpisodeList_episode
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Mutation to follow a topic
 */
const followTopicMutation = graphql`
  mutation PodcastPageFollowMutation($input: FollowTopicInput!) {
    followTopic(input: $input) {
      topic {
        id
        isFollowedByViewer
        followerCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

/**
 * Mutation to unfollow a topic
 */
const unfollowTopicMutation = graphql`
  mutation PodcastPageUnfollowMutation($input: UnfollowTopicInput!) {
    unfollowTopic(input: $input) {
      topic {
        id
        isFollowedByViewer
        followerCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

interface PodcastContentProps {
  topic: PodcastPage_topic$key;
}

function PodcastContent({ topic: topicRef }: PodcastContentProps) {
  const { playEpisode, currentEpisode, isPlaying, setIsPlaying } = usePlayer();
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: topic, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    PodcastPage_topic,
    topicRef
  );

  const [commitFollow, isFollowingMutation] = useMutation<PodcastPageFollowMutation>(followTopicMutation);
  const [commitUnfollow, isUnfollowingMutation] = useMutation<PodcastPageUnfollowMutation>(unfollowTopicMutation);

  const isFollowing = topic.isFollowedByViewer === true;
  const isMutationPending = isFollowingMutation || isUnfollowingMutation;

  const handleFollow = useCallback(() => {
    commitFollow({
      variables: {
        input: { topicSlug: topic.slug },
      },
      optimisticUpdater: (store) => {
        const topicRecord = store.get(topic.id);
        if (topicRecord) {
          const currentFollowerCount = topicRecord.getValue('followerCount') as number | null;
          topicRecord.setValue(true, 'isFollowedByViewer');
          topicRecord.setValue((currentFollowerCount ?? 0) + 1, 'followerCount');
        }
      },
      onError: (error) => {
        console.error('Failed to follow topic:', error);
      },
    });
  }, [commitFollow, topic.slug, topic.id]);

  const handleUnfollow = useCallback(() => {
    commitUnfollow({
      variables: {
        input: { topicSlug: topic.slug },
      },
      optimisticUpdater: (store) => {
        const topicRecord = store.get(topic.id);
        if (topicRecord) {
          const currentFollowerCount = topicRecord.getValue('followerCount') as number | null;
          topicRecord.setValue(false, 'isFollowedByViewer');
          topicRecord.setValue(Math.max(0, (currentFollowerCount ?? 0) - 1), 'followerCount');
        }
      },
      onError: (error) => {
        console.error('Failed to unfollow topic:', error);
      },
    });
  }, [commitUnfollow, topic.slug, topic.id]);

  // Extract episode refs from the connection for EpisodeList
  const episodeRefs = useMemo(() => {
    return topic.episodes.edges.map((edge) => edge.node);
  }, [topic.episodes.edges]);

  const handleSelectEpisode = (episodeData: EpisodeList_episode$data) => {
    const isCurrentEpisode = currentEpisode?.episodeId === episodeData.episodeId;
    if (isCurrentEpisode) {
      // Toggle play/pause for current episode
      setIsPlaying(!isPlaying);
    } else {
      // Play new episode using the typed data from EpisodeList
      playEpisode({
        episodeId: episodeData.episodeId,
        topicSlug: topic.slug,
        topicName: topic.name,
        title: episodeData.title,
        summary: episodeData.summary,
        audioS3Key: episodeData.audioS3Key,
        durationSeconds: episodeData.durationSeconds,
        publishedAt: episodeData.publishedAt,
        chapters: episodeData.chapters.map((ch) => ({
          id: ch.id,
          title: ch.title,
          startTimeMs: ch.startTimeMs,
          endTimeMs: ch.endTimeMs,
        })),
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title={topic.name}
        description={`Listen to ${topic.name} - AI-generated podcast episodes from your favorite content sources.`}
      />
      <Header
        tenant={{
          slug: topic.slug,
          name: topic.name,
          spotifyUrl: topic.spotifyUrl ?? undefined,
          applePodcastsUrl: topic.applePodcastsUrl ?? undefined,
          s3AnchorImagePath: topic.s3AnchorImagePath ?? undefined,
        }}
      />

      <main className="mx-auto max-w-4xl px-4 py-8">
        {/* Follow/Unfollow section - only visible when authenticated */}
        {isAuthenticated && (
          <div className="mb-6 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {topic.followerCount} follower{topic.followerCount !== 1 ? 's' : ''}
              </span>
            </div>
            <button
              type="button"
              onClick={isFollowing ? handleUnfollow : handleFollow}
              disabled={isMutationPending}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isFollowing
                  ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  : 'bg-indigo-600 text-white hover:bg-indigo-500'
              }`}
            >
              {isMutationPending ? (
                <>
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {isFollowing ? 'Unfollowing...' : 'Following...'}
                </>
              ) : isFollowing ? (
                <>
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                  Following
                </>
              ) : (
                <>
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Follow
                </>
              )}
            </button>
          </div>
        )}

        <Search value={searchQuery} onChange={setSearchQuery} />

        <EpisodeList
          episodes={episodeRefs}
          searchQuery={searchQuery}
          onSelectEpisode={handleSelectEpisode}
          currentEpisodeId={currentEpisode?.episodeId}
          isPlaying={isPlaying}
          topicSlug={topic.slug}
          topicName={topic.name}
          showLikeButtons={isAuthenticated}
        />

        {hasNext && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => loadNext(20)}
              disabled={isLoadingNext}
              className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingNext ? 'Loading...' : 'Load More Episodes'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function PodcastPageLoader({ slug }: { slug: string }) {
  const data = useLazyLoadQuery<PodcastPageQuery>(podcastPageQuery, { slug });

  if (!data.topic) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
        <SEO title="Podcast Not Found" noIndex />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Podcast Not Found
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            This podcast doesn't exist or is not available.
          </p>
          <Link
            to="/"
            className="mt-4 inline-block text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
          >
            Browse all podcasts
          </Link>
        </div>
      </div>
    );
  }

  return <PodcastContent topic={data.topic} />;
}

export function PodcastPage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          Invalid podcast URL
        </div>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <div className="text-lg text-gray-600 dark:text-gray-400">Loading...</div>
        </div>
      }
    >
      <PodcastPageLoader slug={slug} />
    </Suspense>
  );
}
